// Shared helpers for rendering profile fields in read-only views.

export interface OptionLike {
  labels: [number, Record<string, string>][];
}

/**
 * Builds a map keyed by the option's `field` name from the Redux options slice
 * (which is keyed by numeric id).
 */
export const buildOptionsMap = (
  allOptions: Record<number | string, { field?: string; labels?: any }>
): Record<string, OptionLike> => {
  const map: Record<string, OptionLike> = {};
  Object.keys(allOptions).forEach((key) => {
    const option = allOptions[key as keyof typeof allOptions];
    if (option?.field) {
      map[option.field] = option as OptionLike;
    }
  });
  return map;
};

/**
 * Decodes a raw profile field value into a human-readable string.
 * Handles binary bitmask options (multi-select) and single-select options;
 * falls back to the raw value when no option metadata is available.
 */
export const decodeFieldValue = (
  fieldKey: string,
  value: any,
  optionsMap: Record<string, OptionLike>
): string => {
  if (value === null || value === undefined || value === '') return '';

  const option = optionsMap[fieldKey];
  if (option?.labels?.length) {
    const labels = option.labels;
    const isBinary = labels[1]?.[0] <= 2;

    if (isBinary) {
      return labels
        .reduce((acc: string[], cur) => {
          if (Number(value) & Math.pow(2, cur[0])) {
            acc.push(cur[1].cn || cur[1].en);
          }
          return acc;
        }, [])
        .join(', ');
    }

    const match = labels.find((label) => label[0] === value);
    return match?.[1]?.cn || match?.[1]?.en || String(value);
  }

  return String(value);
};

export interface FieldGroup {
  title: string;
  keys: string[];
}

/** Ordered, named groups for the read-only profile view. */
export const FIELD_GROUPS: FieldGroup[] = [
  {
    title: 'Basics',
    keys: [
      'name',
      'gender',
      'birthday',
      'age',
      'height',
      'city',
      'state',
      'country',
      'postalCode',
      'homeTown',
      'zodiac',
      'chineseZodiac',
    ],
  },
  {
    title: 'Background',
    keys: [
      'education',
      'occupation',
      'ethnicity',
      'languages',
      'religion',
      'residentStatus',
      'maritalStatus',
      'haveChildren',
    ],
  },
  { title: 'Lifestyle', keys: ['smoking', 'drinking', 'drug', 'spending'] },
  { title: 'Bio', keys: ['summary'] },
];

const EXCLUDED_PROFILE_KEYS = new Set([
  'id',
  'userId',
  'createdAt',
  'updatedAt',
  'answers',
  'extraData',
  'hiddenProfileFields',
]);

/**
 * Buckets the keys of a profile object into ordered, named groups:
 * the explicit FIELD_GROUPS, a "Preferences" group for any `*Seek*` keys,
 * and an "Other" group for everything left over.
 */
export const groupProfileKeys = (
  profile: Record<string, any>
): FieldGroup[] => {
  const allKeys = Object.keys(profile).filter(
    (key) => !EXCLUDED_PROFILE_KEYS.has(key)
  );
  const assigned = new Set<string>();
  const result: FieldGroup[] = [];

  FIELD_GROUPS.forEach((group) => {
    const keys = group.keys.filter((key) => allKeys.includes(key));
    keys.forEach((key) => assigned.add(key));
    if (keys.length) result.push({ title: group.title, keys });
  });

  const preferenceKeys = allKeys.filter(
    (key) => !assigned.has(key) && key.includes('Seek')
  );
  preferenceKeys.forEach((key) => assigned.add(key));
  if (preferenceKeys.length) {
    result.push({ title: 'Preferences', keys: preferenceKeys });
  }

  const otherKeys = allKeys.filter((key) => !assigned.has(key));
  if (otherKeys.length) result.push({ title: 'Other', keys: otherKeys });

  return result;
};
